/*
 * A single nav section's authenticated landing route. The real ops screens land in their own slices
 * (catalogue browse, contests, settlement, …); until then each visible section shows a placeholder.
 * Deep-link protection lives here too: an operator whose role does not grant the section's
 * permission is bounced to the landing page, so a hand-typed URL can never reach a hidden section
 * (the SideNav already omits it). When `children` is provided, the permitted section renders that
 * content instead of the placeholder (Configuration, CAE-300).
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { EmptyState } from '@cou/admin-ui';
import { AdminShell } from './AdminShell';
import { NoAccessScreen } from './NoAccessScreen';
import { useSectionAccess } from './section-access';

export interface ProtectedSectionProps {
  readonly sectionKey: string;
  /** When set, render this instead of the coming-soon placeholder (still permission-gated). */
  readonly children?: ReactNode;
}

export function ProtectedSection({ sectionKey, children }: ProtectedSectionProps) {
  const access = useSectionAccess(sectionKey);

  if (access.kind === 'dead-end') {
    return <NoAccessScreen />;
  }

  if (access.kind === 'redirect') {
    return <Navigate to={access.to} replace />;
  }

  if (children !== undefined) {
    return <>{children}</>;
  }

  return (
    <AdminShell title={access.section.label}>
      <EmptyState
        variant="page"
        title={`${access.section.label} coming soon`}
        body="This section's tooling lands in a later slice."
      />
    </AdminShell>
  );
}
